// KPI Builder — start from an existing KPI. Its query and config are copied as they are; only the cadre
// (in-service, ANMs only, pre-service) and its matching location filters are swapped, so special KPIs keep working.
import { LOCATION_MAP_IN, LOCATION_MAP_PRE } from 'constants/kpiBuilderCatalogue';
import { splitFilters } from './custom';
import { BuilderAnswers, BuilderConfig, BuilderOutput, TemplateAnswers, TemplateCadre } from './types';

const CADRES: Record<Exclude<TemplateCadre, 'keep'>, string[]> = {
  inService: ['ANM-Bihar', 'ANM-Faculty-Bihar'],
  inServiceAnm: ['ANM-Bihar'],
  preService: ['ANM-Student-Bihar'],
};
const LOCATION_KEYS = [...Object.keys(LOCATION_MAP_PRE), ...Object.keys(LOCATION_MAP_IN)];

type Json = Record<string, unknown>;
const parse = <T,>(text: string, fallback: T): T => {
  try {
    return JSON.parse(text) as T;
  } catch {
    return fallback;
  }
};
const queriesOf = (t: TemplateAnswers): Json[] => {
  const q = parse<unknown>(t.query, []);
  return Array.isArray(q) ? (q as Json[]) : [];
};
/** The stored form keeps requestQueryMap and aggrQuery as JSON strings; they may also already be objects. */
const objectOf = (v: unknown): Json => (typeof v === 'string' ? parse<Json>(v, {}) : ((v as Json) ?? {}));

function professions(node: unknown, found: string[][] = []): string[][] {
  if (Array.isArray(node)) node.forEach((n) => professions(n, found));
  else if (node && typeof node === 'object') {
    Object.entries(node as Json).forEach(([k, v]) => {
      if (k === 'profession' && Array.isArray(v)) found.push(v as string[]);
      else professions(v, found);
    });
  }
  return found;
}

/** Which cadre the copied KPI counts, if it can be told; used to explain what will change. */
export function detectCadre(t: TemplateAnswers): TemplateCadre | 'mixed' | 'none' {
  const all = queriesOf(t).flatMap((q) => professions(objectOf(q.aggrQuery)));
  const names = new Set(all.flat());
  if (names.size === 0) return 'none';
  if (names.has('ANM-Student-Bihar') && names.size === 1) return 'preService';
  if (names.has('ANM-Faculty-Bihar')) return 'inService';
  return names.size === 1 && names.has('ANM-Bihar') ? 'inServiceAnm' : 'mixed';
}

function swapProfession(node: unknown, to: string[]): unknown {
  if (Array.isArray(node)) return node.map((n) => swapProfession(n, to));
  if (!node || typeof node !== 'object') return node;
  return Object.fromEntries(Object.entries(node as Json).map(([k, v]) => [k, k === 'profession' && Array.isArray(v) ? to : swapProfession(v, to)]));
}

/** Pre-service KPIs filter by institute; the others by facility type and block. Other filter keys stay. */
function swapLocation(map: Json, cadre: Exclude<TemplateCadre, 'keep'>): Json {
  const kept = Object.fromEntries(Object.entries(map).filter(([k]) => !LOCATION_KEYS.includes(k)));
  return { ...(cadre === 'preService' ? LOCATION_MAP_PRE : LOCATION_MAP_IN), ...kept };
}

type Clause = Record<string, unknown>;

/** AND the typed conditions into the top-level filter of every aggregation, so each copied query is narrowed. */
function narrowTopLevel(aggr: Json, must: Clause[], mustNot: Clause[]): Json {
  if (!must.length && !mustNot.length) return aggr;
  const aggs = (aggr.aggs ?? {}) as Record<string, Json>;
  const next = Object.fromEntries(
    Object.entries(aggs).map(([name, node]) => [
      name,
      node.filter ? { ...node, filter: { bool: { filter: [node.filter, ...must], ...(mustNot.length ? { must_not: mustNot } : {}) } } } : node,
    ]),
  );
  return { ...aggr, aggs: next };
}

export const isTemplateComplete = (t: TemplateAnswers): boolean => !!t.code && queriesOf(t).length > 0 && !!t.chartType;

export function compileTemplate(a: BuilderAnswers, title: string, code: string): BuilderOutput {
  const t = a.template;
  const swap = t.cadre === 'keep' ? null : t.cadre;
  const typed = splitFilters(a.custom.filters);
  const queries = queriesOf(t).map((q) => {
    const swapped = swap ? swapProfession(objectOf(q.aggrQuery), CADRES[swap]) : objectOf(q.aggrQuery);
    const aggr = narrowTopLevel(swapped as Json, typed.must, typed.mustNot);
    const rqm = swap ? swapLocation(objectOf(q.requestQueryMap), swap) : objectOf(q.requestQueryMap);
    return { ...q, requestQueryMap: JSON.stringify(rqm), aggrQuery: JSON.stringify(aggr) };
  });
  const { _comment, builder: _builder, ...rest } = parse<Json>(t.config, {});
  const config = { ...rest, drillChart: a.drill || 'none', builder: { recipe: 'template', source: null, sourceB: null, learnerGroups: [], measure: null, groupBy: null, topN: 10, minCount: 1, interval: 'day', cumulative: false, rateGroup: null, columns: [], rows: 1000, kpiFilters: [], custom: a.custom, template: { code: t.code, title: t.title, chartType: t.chartType, handlerKey: t.handlerKey, cadre: t.cadre } } } as unknown as BuilderConfig;
  return {
    visualizationCode: code || 'newVisualization', chartType: t.chartType, handlerKey: t.handlerKey || undefined,
    queryString: JSON.stringify(queries), config, queries: queries.map((q) => ({ ...q, requestQueryMap: objectOf(q.requestQueryMap), aggrQuery: objectOf(q.aggrQuery) })) as unknown as BuilderOutput['queries'],
  };
}
